import React from "react";
import {
  YouTube,
  Instagram,
  SportsEsports,
  LiveTv,
  ShieldOutlined,
  BoltOutlined,
  AutoAwesome,
} from "@mui/icons-material";
import obsLogo from "../../images/obs_logo.jpg";
import africanIndependenceNews from "../../images/ait.jpg";

const stats = [
  { value: "1,000+", label: "Creators using Zenstream" },
  { value: "4.9/5", label: "Average creator rating" },
  { value: "12M+", label: "Minutes of content censored" },
  { value: "99.4%", label: "Detection accuracy" },
];

const platforms = [
  {
    name: "YouTube Studio",
    logo: <YouTube className="text-red-600" fontSize="small" />,
  },
  {
    name: "OBS Studio",
    logo: (
      <img
        src={obsLogo}
        alt="OBS Studio logo"
        className="h-5 w-5 rounded-full object-cover"
      />
    ),
  },
  {
    name: "TikTok",
    logo: (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-950 text-[10px] font-bold text-white">
        T
      </span>
    ),
  },
  {
    name: "Kick",
    logo: <SportsEsports className="text-green-500" fontSize="small" />,
  },
  {
    name: "Instagram",
    logo: <Instagram className="text-pink-500" fontSize="small" />,
  },
  {
    name: "RTMP/SRT",
    logo: <LiveTv className="text-zinc-700" fontSize="small" />,
  },
];

const testimonials = [
  {
    logo: africanIndependenceNews,
    logoAlt: "African Independence News logo",
    quote:
      "Zenstream gives us a practical way to monitor risky livestream moments before they become platform issues.",
    name: "African Independence News",
    role: "Broadcast media organization",
    rating: 5,
  },
  {
    quote:
      "OBS connection took 30 seconds. We now run every Sunday service through Zenstream with way less stress.",
    name: "Pastor Daniel R.",
    role: "Church livestream director",
    rating: 5,
  },
  {
    quote:
      "I switched from a hardware delay box. Zenstream is cheaper, faster, and the natural-language triggers are wild.",
    name: "Mira K.",
    role: "Podcast host & YouTuber",
    rating: 5,
  },
  {
    quote:
      "The clip workflow feels like having an assistant who actually understands what moments matter.",
    name: "Jordan M.",
    role: "Gaming creator",
    rating: 5,
  },
  {
    quote:
      "It catches the things our team used to miss during live broadcasts. That alone makes it worth it.",
    name: "Alicia S.",
    role: "Live production manager",
    rating: 5,
  },
  {
    quote:
      "The best part is not rebuilding our workflow. We can keep OBS and still add smarter moderation.",
    name: "Marcus T.",
    role: "Creator operations lead",
    rating: 5,
  },
];

const workflowCards = [
  {
    icon: <ShieldOutlined className="!text-[20px]" />,
    title: "Protect the stream",
    body: "Catch profanity, visual risk, copyrighted audio, and brand-sensitive moments before they become platform issues.",
  },
  {
    icon: <BoltOutlined className="!text-[20px]" />,
    title: "Ship clips faster",
    body: "Turn long streams, podcasts, and videos into safer publish-ready clips without rebuilding your workflow.",
  },
];

function RatingStars({ count = 5 }) {
  return (
    <div
      className="flex gap-1 text-zinc-950"
      aria-label={`${count} star rating`}
    >
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="text-sm leading-none">
          ★
        </span>
      ))}
    </div>
  );
}

function TestimonialCard({ item }) {
  return (
    <figure className="mx-3 flex min-h-[360px] w-[330px] shrink-0 flex-col justify-between rounded-[2rem] border border-zinc-200 bg-white p-6 text-left shadow-[0_18px_60px_rgba(24,24,27,0.08)] sm:min-h-[380px] sm:w-[410px] sm:p-7">
      <div className="min-w-0">
        <div className="mb-5 flex items-center justify-between gap-4">
          <RatingStars count={item.rating} />

          {item.logo ? (
            <img
              src={item.logo}
              alt={item.logoAlt || `${item.name} logo`}
              className="h-10 w-10 shrink-0 rounded-full border border-zinc-200 object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-700">
              {item.name.charAt(0)}
            </div>
          )}
        </div>

        <blockquote className="font-jakarta break-words text-[1.15rem] font-medium leading-[1.32] tracking-[-0.025em] text-zinc-800 sm:text-[1.28rem]">
          “{item.quote}”
        </blockquote>
      </div>

      <figcaption className="mt-7 border-t border-zinc-100 pt-5">
        <div className="break-words font-semibold text-zinc-950">
          {item.name}
        </div>
        <div className="mt-1 break-words text-sm leading-5 text-zinc-500">
          {item.role}
        </div>
      </figcaption>
    </figure>
  );
}

export default function SocialProof() {
  const marqueeItems = [...testimonials, ...testimonials];

  return (
    <section
      aria-label="Social proof — creators trust Zenstream"
      className="relative isolate w-full overflow-hidden bg-white py-20 sm:py-28 font-display"
    >
      <style>
        {`
          @keyframes zenMarquee {
            from {
              transform: translate3d(0, 0, 0);
            }
            to {
              transform: translate3d(-50%, 0, 0);
            }
          }

          .zen-marquee-track {
            width: max-content;
            animation: zenMarquee 44s linear infinite;
            will-change: transform;
          }

          @media (prefers-reduced-motion: reduce) {
            .zen-marquee-track {
              animation: none;
            }
          }
        `}
      </style>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-black sm:text-sm">
            Trusted by creators
          </p>

          <h2 className="font-display mt-4 text-balance text-4xl font-medium tracking-[-0.04em] text-black sm:text-5xl md:text-6xl">
            Built for livestreams where <em className="italic font-medium">one mistake</em> can cost the channel.
          </h2>

          <p className="mx-auto mt-6 max-w-3xl text-base leading-8 text-neutral-600 sm:text-lg">
            Zenstream helps creators, churches, podcasts, and live platforms
            catch profanity, copyrighted audio, explicit visuals, and risky
            moments before they become strikes or brand-safety issues.
          </p>
        </div>

        {/* Stats — black on white, no gradients */}
        <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-3xl border border-black/10 bg-white p-5 text-center transition duration-300 hover:-translate-y-1 sm:p-7"
            >
              <div className="font-display text-3xl font-medium tracking-tight text-black sm:text-4xl">
                {s.value}
              </div>
              <div className="mt-2 text-sm font-medium leading-6 text-neutral-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Minimal workflow panel */}
        <div className="mt-8 rounded-3xl border border-black/10 bg-white p-5 sm:p-7">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="text-left">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-black">
                <AutoAwesome className="!text-[16px] text-black" />
                Workflow friendly
              </div>

              <h3 className="font-display max-w-xl text-3xl font-medium tracking-[-0.03em] text-black sm:text-4xl">
                Keep your tools. Add smarter video safety.
              </h3>

              <p className="mt-4 max-w-2xl text-base leading-7 text-neutral-600">
                Zenstream fits around your existing live and post-production
                stack, so you can moderate, clip, caption, and publish without
                changing how you already work.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {workflowCards.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-black/10 bg-white p-5"
                  >
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-black text-white">
                      {item.icon}
                    </div>

                    <h4 className="font-display text-lg font-semibold tracking-tight text-black">
                      {item.title}
                    </h4>

                    <p className="mt-2 text-sm leading-6 text-neutral-600">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-black/10 bg-white p-5 sm:p-6">
              <p className="text-left text-xs font-semibold uppercase tracking-[0.24em] text-black">
                Works with your stack
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {platforms.map((p) => (
                  <div
                    key={p.name}
                    className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3 text-left transition duration-300 hover:-translate-y-0.5"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 bg-white">
                      {p.logo}
                    </div>
                    <span className="text-sm font-semibold text-black">
                      {p.name}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl border border-black/10 bg-white p-5 text-left">
                <p className="text-sm font-semibold text-black">
                  No workflow rebuild.
                </p>
                <p className="mt-1 text-sm leading-6 text-neutral-500">
                  Add Zenstream to your live pipeline, editing process, or
                  creator stack — keep your existing tools.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Infinite testimonial marquee */}
        <div className="relative left-1/2 mt-14 w-screen -translate-x-1/2 overflow-hidden py-6 sm:mt-16 sm:py-8">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent sm:w-40" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-white to-transparent sm:w-40" />

          <div className="zen-marquee-track flex items-stretch">
            {marqueeItems.map((item, index) => (
              <TestimonialCard key={`${item.name}-${index}`} item={item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}